// icon.service.ts
import { Injectable } from '@angular/core';
import { icon, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faLocationPin,
    faSignalStream,
    faCircleDot,
    faMapMarkerCheck,
    faWarehouse,
    faFlagCheckered,
    faShip,
    faPlane,
    faTruck,
    faTrain,
    faAnchor,
    faAnchorCircleCheck,
    faPlaneDeparture,
    faPlaneArrival,
    faBoxTaped,
    faBoxOpen,
    faWater,
    faCloud,
    faRoad,
    faTrainTrack,
    faLocationDot,
} from '@fortawesome/pro-solid-svg-icons';

@Injectable({
    providedIn: 'root',
})
export class IconService {
    private iconMap: Record<string, IconDefinition> = {
        pin: faLocationPin,
        radar: faSignalStream,
        dot: faCircleDot,
        check: faMapMarkerCheck,
        port: faLocationDot,
        place: faWarehouse,
        warehouse: faWarehouse,
        flag: faFlagCheckered,
        ship: faShip,
        plane: faPlane,
        truck: faTruck,
        train: faTrain,
        anchor: faAnchor,
        'anchor-check': faAnchorCircleCheck,
        'plane-departure': faPlaneDeparture,
        'plane-arrival': faPlaneArrival,
        'box-taped': faBoxTaped,
        'box-open': faBoxOpen,
        water: faWater,
        cloud: faCloud,
        road: faRoad,
        'train-track': faTrainTrack,
    };

    getSvgDataUrl(iconType: string, color: string): string {
        const faIcon = this.iconMap[iconType] || this.iconMap['pin'];
        const rendered = icon(faIcon);

        if (!rendered || !rendered.html || !rendered.html[0]) {
            return this.getFallbackSvg(color);
        }

        const svg = rendered.html[0];
        const coloredSvg = svg.replace(/fill="[^"]*"/g, `fill="${color}"`);
        const encodedSvg = encodeURIComponent(coloredSvg);

        return `data:image/svg+xml,${encodedSvg}`;
    }

    getSvgPath(iconType: string): { path: string; viewBox: string } {
        const faIcon = this.iconMap[iconType] || this.iconMap['pin'];

        if (!faIcon.icon) {
            return this.getFallbackPath();
        }

        const [width, height, , , svgPath] = faIcon.icon;
        const path = Array.isArray(svgPath) ? svgPath[0] : svgPath;

        return {
            path: path as string,
            viewBox: `0 0 ${width} ${height}`,
        };
    }

    getIconDimensions(iconType: string): {
        width: number;
        height: number;
        isPinStyle: boolean;
    } {
        const faIcon = this.iconMap[iconType] || this.iconMap['pin'];

        if (!faIcon.icon) {
            return { width: 512, height: 512, isPinStyle: true };
        }

        const [width, height] = faIcon.icon;
        const isPinStyle = [
            'pin',
            'check',
            'warehouse',
            'flag',
            'anchor',
            'anchor-check',
        ].includes(iconType);

        return { width, height, isPinStyle };
    }

    private getFallbackSvg(color: string): string {
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='${encodeURIComponent(color)}' d='M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z'/%3E%3C/svg%3E`;
    }

    private getFallbackPath(): { path: string; viewBox: string } {
        return {
            path: 'M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z',
            viewBox: '0 0 512 512',
        };
    }
}
